import axios from "axios";
import { API_URL } from "../globals/utils/constants";
import logger from "../globals/utils/logger";
import { clearClientSession } from "../globals/utils/authSession";

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed() {
  refreshSubscribers.forEach(({ onSuccess }) => onSuccess());
  refreshSubscribers = [];
}

function onRefreshFailed(refreshError) {
  refreshSubscribers.forEach(({ onFailure }) => onFailure(refreshError));
  refreshSubscribers = [];
}

function addRefreshSubscriber(onSuccess, onFailure) {
  refreshSubscribers.push({ onSuccess, onFailure });
}

export const API_TIMEOUT_MS = 30000;

const httpClient = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: API_TIMEOUT_MS,
});

httpClient.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    // Default axios instance Content-Type is application/json, which serializes
    // FormData as JSON and drops the file bytes (backend then raises KeyError 'file').
    delete config.headers["Content-Type"];
  }

  if (!config.headers["X-Request-ID"]) {
    config.headers["X-Request-ID"] = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    logger.error("apiClient", "API request failed", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      throw error;
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        addRefreshSubscriber(
          () => resolve(httpClient(originalRequest)),
          reject
        );
      });
    }

    isRefreshing = true;

    try {
      await axios.post(
        `${API_URL}/api/v1/token/refresh`,
        {},
        { withCredentials: true }
      );

      isRefreshing = false;
      onRefreshed();

      return httpClient(originalRequest);
    } catch (refreshError) {
      onRefreshFailed(refreshError);
      isRefreshing = false;
      clearClientSession();
      window.location.href = "/";
      throw refreshError;
    }
  }
);

export default httpClient;

function _pathEndsWithNullish(endpoint) {
  return /\/(null|undefined)\/?(\?|$)/.test(String(endpoint ?? ""));
}

export const apiClient = async (
  endpoint,
  method = "GET",
  data = {},
  params = {},
  headers = {}
) => {
  if (_pathEndsWithNullish(endpoint)) {
    logger.warn("apiClient", "Skipping request with nullish path segment", {
      endpoint,
      method,
    });
    return [];
  }
  const response = await httpClient({
    url: endpoint,
    method,
    data,
    params,
    headers,
    timeout: API_TIMEOUT_MS,
  });
  return response.data;
};

export const apiClientRaw = async (
  endpoint,
  method = "GET",
  data = {},
  params = {},
  headers = {}
) => {
  if (_pathEndsWithNullish(endpoint)) {
    logger.warn("apiClientRaw", "Skipping request with nullish path segment", {
      endpoint,
      method,
    });
    return { data: [], status: 204 };
  }
  const response = await httpClient({
    url: endpoint,
    method,
    data,
    params,
    headers,
    validateStatus: () => true,
    timeout: API_TIMEOUT_MS,
  });
  return response;
};
